import * as React from "react";
import { NavLink, useLocation } from "react-router";
import { BellRing, ChevronRight, LayoutDashboard, ListChecks, Search, ShieldCheck } from "lucide-react";
import { cn, Eyebrow, Pill } from "@ozpanel/ui";
import { CATEGORY_ORDER, categoryLabel, categoryIcon, STATIC_NAV_ITEMS } from "../categories";
import type { ModuleRow } from "../modules";

// Sidebar — V6.7's registry-driven nav, rebuilt.
//
// The first version was a flat, un-collapsible, un-filterable scroll of ~47 text links under
// 11 headings. That is unusable as a way to FIND anything, and is most of what made the
// console feel primitive. This adds: a filter over the nav itself, collapsible groups that
// remember their state, the active group auto-opened, icons per group, and a health dot only
// where a module actually reports one.

export interface NavItem {
  key: string;
  label: string;
  path: string;
  category: string;
  health?: string;
}

export function buildNav(items: ModuleRow[], role: string): Map<string, NavItem[]> {
  const groups = new Map<string, NavItem[]>();
  const push = (item: NavItem) => {
    const list = groups.get(item.category) ?? [];
    list.push(item);
    groups.set(item.category, list);
  };
  for (const item of items) {
    const tiers = item.manifest.tiers ?? [];
    if (!tiers.includes(role)) continue; // BP-S6.4: an empty/mismatched Tiers hides a module, correctly
    push({
      key: `module:${item.manifest.name}`,
      label: item.manifest.title,
      path: `/modules/${item.manifest.name}`,
      category: item.manifest.category || "",
      health: item.health,
    });
  }
  for (const item of STATIC_NAV_ITEMS) {
    if (!item.roles.includes(role)) continue;
    push({ key: `static:${item.key}`, label: item.label, path: item.path, category: item.category });
  }
  for (const list of groups.values()) list.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}

const STORAGE_KEY = "ozpanel.nav.collapsed";

export function Sidebar({
  groups,
  loading,
  degradedCount,
}: {
  groups: Map<string, NavItem[]>;
  loading?: boolean;
  degradedCount?: number;
}) {
  const location = useLocation();
  const [filter, setFilter] = React.useState("");
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });

  const toggle = (cat: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      } catch { /* storage disabled — collapse still works for this session */ }
      return next;
    });

  const q = filter.trim().toLowerCase();
  const ordered = React.useMemo(() => {
    const seen = new Set(groups.keys());
    const known = CATEGORY_ORDER.filter((c) => seen.has(c));
    const extra = [...seen].filter((c) => !(CATEGORY_ORDER as readonly string[]).includes(c)).sort();
    return [...known, ...extra];
  }, [groups]);

  return (
    <aside className="flex h-full w-[232px] flex-none flex-col border-r border-line">
      <div className="p-3 pb-2">
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-faint" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a section…"
            aria-label="Filter navigation"
            className="w-full rounded-lg border border-line bg-bg2 py-[7px] pr-2.5 pl-[30px] text-[13px] text-fg outline-none placeholder:text-faint focus:border-accent"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        <Item to="/" end icon={<LayoutDashboard size={15} />} label="Dashboard" />
        <Item to="/jobs" icon={<ListChecks size={15} />} label="Jobs" />
        <Item to="/config-check" icon={<ShieldCheck size={15} />} label="Self-check" />
        <Item to="/alerts" icon={<BellRing size={15} />} label="Open alerts" />

        {loading && <div className="px-2 py-3 text-[12px] text-faint">Loading modules…</div>}

        {ordered.map((cat) => {
          const all = groups.get(cat) ?? [];
          const items = q ? all.filter((i) => i.label.toLowerCase().includes(q)) : all;
          if (items.length === 0) return null;
          // A filter that leaves a group collapsed hides its own matches, so filtering
          // implicitly opens everything it matched.
          const isCollapsed = !q && collapsed.has(cat);
          const hasActive = items.some((i) => location.pathname.startsWith(i.path));
          const Icon = categoryIcon(cat);
          return (
            <div key={cat || "uncategorized"} className="mt-3">
              <button
                type="button"
                onClick={() => toggle(cat)}
                className="group flex w-full items-center gap-1.5 rounded px-2 py-1 text-left hover:bg-surface2"
              >
                <ChevronRight
                  size={11}
                  className={cn(
                    "shrink-0 text-faint transition-transform",
                    !isCollapsed && "rotate-90",
                  )}
                />
                <Icon size={12} className={cn("shrink-0", hasActive ? "text-accent" : "text-faint")} />
                <Eyebrow className={cn("truncate", hasActive && "text-accent2")}>{categoryLabel(cat)}</Eyebrow>
              </button>
              {!isCollapsed && (
                <div className="mt-0.5">
                  {items.map((item) => (
                    <Item key={item.key} to={item.path} label={item.label} health={item.health} indent />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {q && ordered.every((c) => !(groups.get(c) ?? []).some((i) => i.label.toLowerCase().includes(q))) && (
          <p className="px-2 py-4 text-[12.5px] text-muted">Nothing matches “{filter}”.</p>
        )}
      </nav>

      {degradedCount !== undefined && degradedCount > 0 && (
        <NavLink
          to="/modules"
          className="mx-2 mb-2 flex items-center justify-between rounded-lg border border-line bg-surface px-2.5 py-2 hover:border-[var(--ozp-accent-line)]"
        >
          <Eyebrow>Needs attention</Eyebrow>
          <Pill tone="warn">{degradedCount}</Pill>
        </NavLink>
      )}
    </aside>
  );
}

function Item({
  to,
  label,
  icon,
  health,
  indent,
  end,
}: {
  to: string;
  label: string;
  icon?: React.ReactNode;
  health?: string;
  indent?: boolean;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-2 rounded-[7px] px-2 py-[6px] text-[13.5px] font-medium transition-colors",
          indent && "pl-[26px]",
          isActive ? "bg-accentsoft text-fg" : "text-muted hover:bg-surface2 hover:text-fg",
        )
      }
    >
      {icon}
      <span className="truncate">{label}</span>
      {health && health !== "healthy" && (
        <span
          title={health}
          className={cn(
            "ml-auto size-1.5 shrink-0 rounded-full",
            health === "critical" ? "bg-crit" : health === "degraded" ? "bg-warn" : "bg-faint",
          )}
        />
      )}
    </NavLink>
  );
}
